import { manifestEntryFor, variantUrl } from '../lib/imageVariants.js';

function buildSrcSet(entry, format) {
  return entry.widths.map((w) => `${variantUrl(entry.stem, w, format)} ${w}w`).join(', ');
}

/**
 * Drop-in replacement for <img src="/images/foo.jpg" alt="…" />.
 *
 * Looks the file up in the build-time manifest (generated by
 * scripts/optimize-images.mjs) and emits a <picture> with AVIF and WebP
 * srcsets at every available width, plus an <img> tag with the original as
 * the universal fallback. Browsers download exactly ONE image — the
 * smallest variant in the best format their viewport will accept.
 *
 * Props:
 *   src              — original /images/foo.jpg path (manifest key derived from it)
 *   alt
 *   sizes            — CSS `sizes` attribute. Default 100vw is fine for
 *                      hero-style images; pass e.g. "(max-width: 720px) 50vw, 25vw"
 *                      for grids so the browser picks a smaller variant on phones.
 *   eager            — set true for above-the-fold images (skips lazy loading
 *                      and adds fetchpriority=high)
 *   className, style, viewTransitionName, ...rest passed through to <img>
 *
 * If the image is not in the manifest (e.g. a freshly added asset), this
 * silently falls back to a plain <img> so nothing breaks.
 */
export function ResponsivePicture({
  src,
  alt = '',
  sizes = '100vw',
  eager = false,
  className,
  style,
  viewTransitionName,
  width,
  height,
  ...rest
}) {
  const entry = manifestEntryFor(src);
  const imgStyle = viewTransitionName ? { ...style, viewTransitionName } : style;
  const loading = eager ? 'eager' : 'lazy';

  if (!entry) {
    return (
      <img
        src={src}
        alt={alt}
        className={className}
        style={imgStyle}
        loading={loading}
        decoding="async"
        width={width}
        height={height}
        {...rest}
      />
    );
  }

  const avifSrcSet = buildSrcSet(entry, 'avif');
  const webpSrcSet = buildSrcSet(entry, 'webp');

  return (
    <picture>
      <source type="image/avif" srcSet={avifSrcSet} sizes={sizes} />
      <source type="image/webp" srcSet={webpSrcSet} sizes={sizes} />
      <img
        src={src}
        alt={alt}
        className={className}
        style={imgStyle}
        loading={loading}
        decoding="async"
        width={width || entry.originalWidth}
        height={height || entry.originalHeight || undefined}
        {...rest}
      />
    </picture>
  );
}
